import { Stack, Box, Typography, Chip } from "@mui/material";
import type { ContentShowQueryQueryType } from "@/types/graphql/admin.generated";
import { ContentProcessingStatusType } from "@/admin/entities/contents";
import { CustomCellDate } from "@/shared/components";

interface ContentShowProps {
  content?: ContentShowQueryQueryType["content"];
  editData?: unknown;
  id?: string;
}

const statusLabels: Record<ContentProcessingStatusType, string> = {
  [ContentProcessingStatusType.NofileType]: "Нет файла",
  [ContentProcessingStatusType.ProcessingType]: "Обработка",
  [ContentProcessingStatusType.CompleteType]: "Готово",
  [ContentProcessingStatusType.ErrorType]: "Ошибка",
};

const statusColors: Record<
  ContentProcessingStatusType,
  "default" | "info" | "success" | "error"
> = {
  [ContentProcessingStatusType.NofileType]: "default",
  [ContentProcessingStatusType.ProcessingType]: "info",
  [ContentProcessingStatusType.CompleteType]: "success",
  [ContentProcessingStatusType.ErrorType]: "error",
};

/**
 * Компонент отображения деталей единицы контента (read-only)
 */
export function ContentShow({
  content: contentProp,
  editData,
}: ContentShowProps) {
  const content =
    contentProp || (editData as ContentShowQueryQueryType | undefined)?.content;

  if (!content) {
    return (
      <Box padding={3}>
        <Typography variant="h6" color="error">
          Контент не найден
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Typography variant="h5">{content.title}</Typography>

      {(content.preview || content.verticalPreview) && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {content.preview && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Горизонтальный превью
              </Typography>
              <img
                src={content.preview.url}
                alt=""
                style={{ maxWidth: 320, borderRadius: 4, display: "block" }}
              />
            </Box>
          )}
          {content.verticalPreview && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Вертикальный превью
              </Typography>
              <img
                src={content.verticalPreview.url}
                alt=""
                style={{ maxHeight: 320, borderRadius: 4, display: "block" }}
              />
            </Box>
          )}
        </Box>
      )}

      {content.description && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Описание
          </Typography>
          <Typography variant="body1">{content.description}</Typography>
        </Box>
      )}

      <Box>
        <Typography variant="body2" color="text.secondary">
          Тип
        </Typography>
        <Chip label={content.type} size="small" color="primary" />
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary">
          Статус обработки
        </Typography>
        <Chip
          label={statusLabels[content.processingStatus]}
          color={statusColors[content.processingStatus]}
          size="small"
        />
      </Box>

      {content.transcodingError && (
        <Box>
          <Typography variant="body2" color="error">
            Ошибка трансодирования
          </Typography>
          <Chip
            label={content.transcodingError}
            color="error"
            size="small"
            variant="outlined"
          />
        </Box>
      )}

      {content.platform && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Платформа
          </Typography>
          <Typography variant="body1">{content.platform.title}</Typography>
        </Box>
      )}

      <Box>
        <Typography variant="body2" color="text.secondary">
          Просмотры
        </Typography>
        <Typography variant="body1">{content.totalViews}</Typography>
      </Box>

      {content.tags && content.tags.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Теги
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {content.tags.map((tag) => (
              <Chip key={tag.id} label={tag.title} size="small" />
            ))}
          </Stack>
        </Box>
      )}

      {content.publishAt && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Опубликовано
          </Typography>
          <CustomCellDate date={content.publishAt} />
        </Box>
      )}

      {(content.video || content.pendingVideo || content.duration !== null) && (
        <>
          <Typography variant="h6" mt={2}>
            Видео
          </Typography>

          {(content.video?.fileName || content.video?.uid) && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Идентификатор видео
              </Typography>
              <Typography variant="body1" fontFamily="monospace">
                {content.video?.fileName ?? content.video?.uid}
              </Typography>
            </Box>
          )}

          {content.pendingVideo && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Обрабатывается новое видео
              </Typography>
              <Typography variant="body1" fontFamily="monospace">
                {content.pendingVideo.fileName ?? content.pendingVideo.uid}
              </Typography>
            </Box>
          )}

          {content.duration !== null && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Длительность (сек)
              </Typography>
              <Typography variant="body1">{content.duration}</Typography>
            </Box>
          )}

          {content.video && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Готово к потоковой передаче
              </Typography>
              <Chip
                label={content.video.readyToStream ? "Да" : "Нет"}
                color={content.video.readyToStream ? "success" : "default"}
                size="small"
              />
            </Box>
          )}
        </>
      )}

      {content.stream && (
        <>
          <Typography variant="h6" mt={2}>
            Эфир
          </Typography>

          <Box>
            <Typography variant="body2" color="text.secondary">
              Статус
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={content.stream.status} size="small" />
              {content.stream.liveConnected && (
                <Chip
                  label="Онлайн"
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          {content.stream.startAt && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Начало
              </Typography>
              <CustomCellDate date={content.stream.startAt} />
            </Box>
          )}

          {content.stream.finishAt && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Завершение
              </Typography>
              <CustomCellDate date={content.stream.finishAt} />
            </Box>
          )}

          {content.stream.lastPublishAt && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Последнее подключение
              </Typography>
              <CustomCellDate date={content.stream.lastPublishAt} />
            </Box>
          )}

          {content.stream.lastPublishFinishAt && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Последнее отключение
              </Typography>
              <CustomCellDate date={content.stream.lastPublishFinishAt} />
            </Box>
          )}

          <Box>
            <Typography variant="body2" color="text.secondary">
              Запись
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={content.stream.enableRecord ? "Включена" : "Выключена"}
                color={content.stream.enableRecord ? "success" : "default"}
                size="small"
              />
              {content.stream.autoPublishRecord && (
                <Chip label="Автопубликация" size="small" />
              )}
              {content.stream.dvrEnabled && <Chip label="DVR" size="small" />}
              {content.stream.isChatEnabled && (
                <Chip label="Чат" size="small" />
              )}
            </Stack>
          </Box>

          {content.stream.entrypoint && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Точка входа
              </Typography>
              <Typography variant="body1">
                {content.stream.entrypoint.title}
              </Typography>
            </Box>
          )}

          {content.stream.pull && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Pull-инжест
              </Typography>
              <Typography variant="body1">
                {content.stream.pull.status} · попытка{" "}
                {content.stream.pull.attempt} из{" "}
                {content.stream.pull.maxAttempts}
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {content.stream.pull.sourceUrl}
              </Typography>
              {content.stream.pull.lastError && (
                <Typography variant="body2" color="error">
                  {content.stream.pull.lastError.message}
                </Typography>
              )}
            </Box>
          )}

          {content.stream.lastError && (
            <Box>
              <Typography variant="body2" color="error">
                Последняя ошибка эфира
              </Typography>
              <Typography variant="body1">
                {content.stream.lastError.code}:{" "}
                {content.stream.lastError.message}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="body2" color="text.secondary">
              Cloudflare
            </Typography>
            <Typography variant="body1" fontFamily="monospace">
              {content.stream.cfAccountId} / {content.stream.liveInputId}
            </Typography>
          </Box>

          {content.stream.recordContentId && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Контент записи
              </Typography>
              <Typography variant="body1" fontFamily="monospace">
                {content.stream.recordContentId}
              </Typography>
            </Box>
          )}
        </>
      )}

      {(content.accessStartAt ||
        content.hideInGeneralLists !== null ||
        content.onlyUsers !== null ||
        content.ageRestrictions) && (
        <>
          <Typography variant="h6" mt={2}>
            Доступ
          </Typography>

          {content.accessStartAt && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Начало доступа
              </Typography>
              <CustomCellDate date={content.accessStartAt} />
            </Box>
          )}

          {content.hideInGeneralLists !== null && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Скрыто в основном списке
              </Typography>
              <Chip
                label={content.hideInGeneralLists ? "Да" : "Нет"}
                color={content.hideInGeneralLists ? "warning" : "default"}
                size="small"
              />
            </Box>
          )}

          {content.onlyUsers !== null && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Только для авторизованных
              </Typography>
              <Chip
                label={content.onlyUsers ? "Да" : "Нет"}
                color={content.onlyUsers ? "info" : "default"}
                size="small"
              />
            </Box>
          )}

          {content.ageRestrictions && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Возрастные ограничения
              </Typography>
              <Typography variant="body1">{content.ageRestrictions}</Typography>
            </Box>
          )}
        </>
      )}

      <Typography variant="h6" mt={2}>
        Системная информация
      </Typography>

      <Box>
        <Typography variant="body2" color="text.secondary">
          ID
        </Typography>
        <Typography variant="body1" fontFamily="monospace">
          {content.id}
        </Typography>
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary">
          Дата создания
        </Typography>
        <CustomCellDate date={content.createdAt} />
      </Box>

      {content.updatedAt && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Дата обновления
          </Typography>
          <CustomCellDate date={content.updatedAt} />
        </Box>
      )}

      {content.deletedAt && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Дата удаления
          </Typography>
          <CustomCellDate date={content.deletedAt} />
        </Box>
      )}
    </Stack>
  );
}
